import {
    Checkbox,
    FormControlLabel,
    Grid,
    InputLabel,
    Typography,
    styled,
} from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { ChangeEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import DraggableSelector from "../../../components/draggable-selector";
import { IListItem } from "../../../interface/list-item";
import { IRootState } from "../../../redux/root.reducer";
import { useAppSelector } from "../../../redux/store";
import { canUpdateHiddenColumnDataset } from "../../../service/user-permission";
import TooltipIcon from "../../../shared/component/tooltip-icon";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { UserPreferencesKeys } from "../interface/dto/user-preferences.dto";
import { tooltipsSelector } from "../redux/selectors/tooltips-selector";

interface IOwnProps {
    displayHiddenColumns: boolean;
    allowCustomColumnOrder: boolean;
    onSelect: (
        value: boolean | Array<string>,
        fileFormatType: UserPreferencesKeys,
    ) => void;
    preferredDataFields: Array<string>;
    isLoading: boolean;
}

const selector = createSelector(
    (state: IRootState) => state.dataFields,
    (dataFields) => ({
        dataFields: {
            options: (dataFields.data ?? [" "]).map((value) => ({
                id: value,
                name: value,
            })),
        },
    }),
);
const mapPreferredFields = (list: Array<string>) =>
    list.map((value) => ({
        id: value,
        name: value,
    }));

const ColumnOrder = ({
    className,
    displayHiddenColumns,
    allowCustomColumnOrder,
    preferredDataFields,
    isLoading,
    onSelect,
}: IWithClassName & IOwnProps) => {
    const { dataFields } = useSelector(selector);
    const tooltips = useAppSelector(tooltipsSelector);
    const columnOrderTooltip = tooltips.columnOrder;
    const dataDictionaryTooltip = tooltips.dictionary;
    const datafieldSelections = mapPreferredFields(preferredDataFields);
    const [selectedDataFields, setSelectedDataFields] =
        useState<Array<IListItem>>(datafieldSelections);
    const role = useAppSelector(({ auth }: IRootState) => auth.user?.role);
    const isAuthorized = canUpdateHiddenColumnDataset(role);

    useEffect(() => {
        if (preferredDataFields) {
            const selectedDataFields = mapPreferredFields(preferredDataFields);
            setSelectedDataFields(selectedDataFields);
        }
    }, [preferredDataFields]);

    const onDataFieldsChange = (list: Array<IListItem>) => {
        setSelectedDataFields(list);
        const fields = list.map((value) => value.name);
        onSelect(fields, "preferredDataFields");
    };

    return (
        <div className={className}>
            <div className="column-section">
                <Typography variant="h5">Column Order</Typography>
                <TooltipIcon content={columnOrderTooltip} />
            </div>
            <Grid container className="bottom-padding">
                <Grid item xs={6}>
                    <InputLabel className="line-label">
                        Allow Custom Column Order
                    </InputLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!isLoading && allowCustomColumnOrder}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    !isLoading &&
                                    onSelect(
                                        event.target.checked,
                                        "allowCustomColumnOrder",
                                    )
                                }
                            />
                        }
                        label="All downloads will use this column order, if checked."
                    />
                    {isAuthorized ? (
                        <>
                            <InputLabel className="line-label">
                                Display hidden columns and datasets
                            </InputLabel>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !isLoading && displayHiddenColumns
                                        }
                                        onChange={(
                                            event: ChangeEvent<HTMLInputElement>,
                                        ) =>
                                            !isLoading &&
                                            onSelect(
                                                event.target.checked,
                                                "displayHiddenColumns",
                                            )
                                        }
                                    />
                                }
                                label="Display columns and datasets that are not shown to clients"
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </Grid>
                <Grid item xs={6} className="dictionary">
                    <a
                        className="data-dictionary"
                        href="https://fdreports.atlassian.net/l/cp/1f0smAb5"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <img src="/assets/book.svg" alt="directory" />
                        Data Dictionary
                    </a>
                    <TooltipIcon
                        className="tooltip-container"
                        content={dataDictionaryTooltip}
                    />
                </Grid>
            </Grid>
            <DraggableSelector
                options={dataFields.options}
                onChange={onDataFieldsChange}
                leftBoxHeader="Preferred Column Order"
                rightBoxHeader="Available Data Fields"
                selected={selectedDataFields ?? []}
                addTitle="Add field(s) to Preferred Data-Fields"
                removeTitle="Remove field(s) from Preferred Data-Fields"
                addAllTitle="Add All field(s) to Preferred Data-Fields"
                removeAllTitle="Remove All field(s) from Preferred Data-Fields"
                disabled={!isLoading && !allowCustomColumnOrder}
            />
        </div>
    );
};

export default styled(ColumnOrder)(({ theme }: IWithThemeProps) => ({
    padding: theme?.spacing(2, 2, 4, 4),
    ".column-section": {
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: theme?.palette.dropdown.listColor,
        borderBottom: `12px solid ${
            theme?.palette.search.iconColor ?? "inherit"
        }`,
    },
    ".line-label": {
        fontWeight: "bold",
        color: theme?.palette.dropdown.listColor,
        margin: theme?.spacing(1, 1, 0, 0),
    },
    ".dictionary": {
        textAlign: "end",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 10,
    },
    ".data-dictionary": {
        display: "flex",
        alignItems: "center",
        textDecoration: "none",
        position: "relative",
        top: "50%",
        color: theme?.palette.text.primary,
        marginBottom: 10,
    },
    ".data-dictionary img": {
        verticalAlign: "middle",
        marginRight: 10,
    },
    ".bottom-padding": {
        paddingBottom: theme?.spacing(2),
    },
    ".tooltip-container": {
        display: "flex",
        alignItems: "center",
        marginBottom: 10,
        position: "relative",
        top: "50%",
    },
}));
